import React, { type ReactNode } from 'react';

export interface BoundaryProps {
  fallback?: ReactNode | undefined;
  onError?: ((error: unknown, info: unknown) => void) | undefined;
}

interface BoundaryState {
  hasError: boolean;
}

type BoundaryCoreProps = React.PropsWithChildren<BoundaryProps>;

class BoundaryCore extends React.Component<BoundaryCoreProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    this.props.onError?.(error, info);
    if (process.env.NODE_ENV === 'development') console.error('ErrorBoundary caught:', error, info);
  }

  render() {
    return this.state.hasError ? (this.props.fallback ?? null) : this.props.children;
  }
}

export function ErrorBoundary({
  children,
  fallback = null,
  onError,
}: React.PropsWithChildren<BoundaryProps>): React.JSX.Element {
  return (
    <BoundaryCore fallback={fallback} {...(onError ? { onError } : {})}>
      {children}
    </BoundaryCore>
  );
}
